import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle,
  ExternalLink,
  Loader2,
  Unplug,
  Search,
  RefreshCw,
  FileText,
  Zap,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface Integration {
  name: string;
  category: "ERP" | "AP";
  logo: string;
  connected: boolean;
  description: string;
  demoUsername?: string;
  demoPassword?: string;
  /** Featured integrations get a larger, more prominent card */
  featured?: boolean;
  /** Last sync timestamp (ISO string) for connected integrations */
  lastSynced?: string;
  /** Number of records imported via this integration */
  recordsImported?: number;
}

const defaultIntegrations: Integration[] = [
  {
    name: "SAP",
    category: "ERP",
    logo: "🔷",
    connected: false,
    description: "Enterprise resource planning system",
    // Demo credentials removed for production
    // demoUsername: "sap_admin",
    // demoPassword: "sap_key_2026",
  },
  {
    name: "Oracle NetSuite",
    category: "ERP",
    logo: "🔶",
    connected: false,
    description: "Cloud-based ERP solution",
    // Demo credentials removed for production
    // demoUsername: "netsuite_api",
    // demoPassword: "ns_secret_abc123",
  },
  {
    name: "Microsoft Dynamics",
    category: "ERP",
    logo: "🟦",
    connected: false,
    description: "Business applications platform",
    demoUsername: "dynamics_user",
    demoPassword: "dyn_pass_xyz",
  },
  {
    name: "QuickBooks",
    category: "AP",
    logo: "🟩",
    connected: true,
    description: "Accounting & AP automation — invoices sync automatically every 15 minutes",
    demoUsername: "qb_admin@company.com",
    demoPassword: "qb_api_key_789",
    featured: true,
    lastSynced: new Date(Date.now() - 2 * 60 * 1000).toISOString(), // 2 min ago
    recordsImported: 47,
  },
  {
    name: "Bill.com",
    category: "AP",
    logo: "🟪",
    connected: false,
    description: "Accounts payable automation",
    demoUsername: "bill_api",
    demoPassword: "bill_secret_456",
  },
  {
    name: "Coupa",
    category: "AP",
    logo: "🔴",
    connected: false,
    description: "Spend management platform",
    demoUsername: "coupa_integrator",
    demoPassword: "coupa_token_def",
  },
];

const categories = ["All", "ERP", "AP"] as const;

export function IntegrationsTab() {
  const [integrations, setIntegrations] = useState<Integration[]>(defaultIntegrations);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [search, setSearch] = useState("");
  const { toast } = useToast();

  const connectedCount = integrations.filter((i) => i.connected).length;

  const filtered = integrations
    .filter((i) => activeCategory === "All" || i.category === activeCategory)
    .filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.connected === b.connected ? 0 : a.connected ? -1 : 1));

  const handleIntegrationClick = (integration: Integration) => {
    setSelectedIntegration(integration);
    // Clear form when selecting integration
    setUsername("");
    setPassword("");
  };

  const handleConnect = async () => {
    if (!selectedIntegration) return;
    setIsConnecting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIntegrations((prev) =>
      prev.map((i) => (i.name === selectedIntegration.name ? { ...i, connected: true } : i))
    );
    toast({
      title: "Connected",
      description: `${selectedIntegration.name} has been connected successfully.`,
    });
    setIsConnecting(false);
    setSelectedIntegration(null);
  };

  const handleDisconnect = async () => {
    if (!selectedIntegration) return;
    setIsConnecting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIntegrations((prev) =>
      prev.map((i) => (i.name === selectedIntegration.name ? { ...i, connected: false } : i))
    );
    toast({
      title: "Disconnected",
      description: `${selectedIntegration.name} has been disconnected.`,
    });
    setIsConnecting(false);
    setSelectedIntegration(null);
  };

  return (
    <>
      <div className="space-y-5">
        {/* Header stats */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-display font-semibold text-foreground tracking-tight">
              Connected Systems
            </h2>
            <p className="text-sm text-muted-foreground">
              {connectedCount} of {integrations.length} integrations active
            </p>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search integrations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>

        {/* Category filters */}
        <div className="flex gap-2">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={activeCategory === cat ? "default" : "outline"}
              size="sm"
              className="h-8 text-xs"
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
              {cat !== "All" && (
                <span className="ml-1 text-[10px] opacity-70">
                  ({integrations.filter((i) => i.category === cat).length})
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Featured (QuickBooks) — shown above the grid when visible */}
        {filtered
          .filter((i) => i.featured)
          .map((integration) => (
            <Card
              key={integration.name}
              onClick={() => handleIntegrationClick(integration)}
              className="relative p-6 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 bg-success/5 border-success/30 ring-1 ring-success/20"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center flex-shrink-0 text-2xl">
                    {integration.logo}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-base font-semibold text-foreground">{integration.name}</p>
                      <Badge className="text-[10px] bg-success/15 text-success border-success/30 border px-2 py-0 font-medium">
                        Live
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {integration.category}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{integration.description}</p>
                  </div>
                </div>
                <div className="flex sm:flex-col items-center sm:items-end gap-4 sm:gap-2 flex-shrink-0">
                  <div className="flex items-center gap-1.5 text-[11px] text-success font-medium">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Connected
                  </div>
                  {integration.lastSynced && (
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <RefreshCw className="w-3 h-3" />
                      Last synced 2 min ago
                    </div>
                  )}
                </div>
              </div>
              {integration.recordsImported !== undefined && (
                <div className="mt-4 pt-4 border-t border-success/20 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                      Invoices imported
                    </p>
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-primary" />
                      <span className="text-sm font-semibold text-foreground font-display">
                        {integration.recordsImported}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                      Sync cadence
                    </p>
                    <div className="flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 text-primary" />
                      <span className="text-sm font-semibold text-foreground font-display">
                        15 min
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                      Auto-offers
                    </p>
                    <div className="flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-primary" />
                      <span className="text-sm font-semibold text-foreground font-display">
                        Enabled
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}

        {/* Grid — non-featured integrations */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered
            .filter((i) => !i.featured)
            .map((integration) => (
              <Card
                key={integration.name}
                onClick={() => handleIntegrationClick(integration)}
                className={`relative p-5 transition-all cursor-pointer hover:shadow-lg hover:-translate-y-0.5 ${
                  integration.connected
                    ? "bg-success/5 border-success/30 ring-1 ring-success/20"
                    : "hover:border-primary/40"
                }`}
              >
                {integration.connected ? (
                  <CheckCircle className="absolute top-3 right-3 w-4 h-4 text-success" />
                ) : (
                  <Badge
                    variant="outline"
                    className="absolute top-3 right-3 text-[9px] px-1.5 py-0 text-muted-foreground border-muted-foreground/30"
                  >
                    Inactive
                  </Badge>
                )}
                <div className="text-3xl mb-3">{integration.logo}</div>
                <p
                  className={`text-sm font-semibold ${integration.connected ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {integration.name}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                  {integration.description}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {integration.category}
                  </Badge>
                  {integration.connected && (
                    <span className="flex items-center gap-1 text-[10px] text-success font-medium">
                      Active <ExternalLink className="w-2.5 h-2.5" />
                    </span>
                  )}
                </div>
              </Card>
            ))}
          {filtered.filter((i) => !i.featured).length === 0 &&
            filtered.filter((i) => i.featured).length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground text-sm">
                No integrations found
              </div>
            )}
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedIntegration} onOpenChange={() => setSelectedIntegration(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{selectedIntegration?.logo}</span>
              {selectedIntegration?.connected ? "Manage" : "Connect"} {selectedIntegration?.name}
            </DialogTitle>
            <DialogDescription>{selectedIntegration?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedIntegration?.featured ? (
              /* QuickBooks connected-state view */
              <div className="space-y-3">
                <div className="p-3 bg-success/10 rounded-lg flex items-center gap-2 text-sm text-success">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Connected and syncing · Last sync 2 min ago</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Invoices imported", value: "47" },
                    { label: "Sync cadence", value: "Every 15 min" },
                    { label: "Auto-offers", value: "Enabled" },
                    { label: "Connection type", value: "OAuth 2.0" },
                  ].map((stat) => (
                    <div key={stat.label} className="p-3 bg-muted/40 rounded-lg">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                        {stat.label}
                      </p>
                      <p className="text-sm font-semibold text-foreground">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Standard credential form for non-connected integrations */
              <>
                <div className="space-y-2">
                  <Label htmlFor="tab-int-username">Username / API Key</Label>
                  <Input
                    id="tab-int-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={`Enter your ${selectedIntegration?.name} username`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tab-int-password">Password / Secret</Label>
                  <Input
                    id="tab-int-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                {selectedIntegration?.connected && (
                  <div className="p-3 bg-success/10 rounded-lg flex items-center gap-2 text-sm text-success">
                    <CheckCircle className="w-4 h-4" />
                    Currently connected
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            {selectedIntegration?.connected && !selectedIntegration.featured && (
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                disabled={isConnecting}
                className="mr-auto"
              >
                {isConnecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Unplug className="w-4 h-4 mr-2" />
                )}
                Disconnect
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedIntegration(null)}>
              {selectedIntegration?.featured ? "Close" : "Cancel"}
            </Button>
            {!selectedIntegration?.featured && (
              <Button onClick={handleConnect} disabled={isConnecting || !username}>
                {isConnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {selectedIntegration?.connected ? "Saving..." : "Connecting..."}
                  </>
                ) : selectedIntegration?.connected ? (
                  "Save Changes"
                ) : (
                  "Connect"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
